import { useState, useEffect, useCallback } from 'react';
import { Shield, AlertTriangle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { SeverityBadge } from './SEOOSShared';

const API = '';

export default function SEOOSTechnical() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('admin_token');

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/seoos/technical`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data || data.error) return <div className="text-center py-16 text-slate-500">{data?.error || 'Loading...'}</div>;

  const { overall_score, category_scores, issues, stats } = data;

  const grouped = {};
  (issues || []).forEach(i => {
    const cat = i.issue || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(i);
  });

  return (
    <div className="space-y-6" data-testid="seoos-technical">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
          <Shield className="w-5 h-5 inline mr-2 text-purple-400" />Technical SEO Audit
        </h2>
        <span className={`text-3xl font-black ${overall_score >= 80 ? 'text-emerald-400' : overall_score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{overall_score}/100</span>
      </div>

      {/* Category Scores */}
      <div className="grid grid-cols-6 gap-3">
        {Object.entries(category_scores || {}).map(([key, score]) => (
          <div key={key} className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-4 text-center">
            <div className={`text-2xl font-black ${score >= 80 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{score}</div>
            <p className="text-[10px] text-slate-500 uppercase mt-1">{key}</p>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {Object.entries(stats || {}).map(([key, val]) => (
          <div key={key} className="bg-slate-900/30 border border-slate-800/30 rounded-lg px-3 py-2 flex justify-between items-center">
            <span className="text-xs text-slate-400">{key.replace(/_/g, ' ')}</span>
            <span className="text-sm font-bold text-white tabular-nums">{val}</span>
          </div>
        ))}
      </div>

      {/* Issues by Category */}
      <div className="space-y-4">
        {Object.entries(grouped).map(([cat, catIssues]) => (
          <div key={cat} className="bg-slate-900/40 border border-slate-800/50 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-800/30 flex items-center justify-between">
              <span className="text-sm font-bold text-white uppercase">{cat.replace(/_/g, ' ')}</span>
              <span className="text-xs text-slate-500">{catIssues.length} issues</span>
            </div>
            <div className="divide-y divide-slate-800/20">
              {catIssues.map((issue, i) => (
                <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                  <SeverityBadge severity={issue.severity} />
                  <span className="text-xs text-slate-300 flex-1">{issue.detail || issue.issue}</span>
                  {issue.count && <span className="text-xs font-bold text-white tabular-nums">{issue.count}</span>}
                  {issue.page && <span className="text-[10px] text-slate-600 truncate max-w-[200px]">{issue.page}</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
